import { QuestionSettingsFields } from '@/components/FormBuilder/QuestionSettingsFields'
import { QuestionTypeSelector } from '@/components/FormBuilder/QuestionTypeSelector'
import { CustomerFieldBindingSettings } from '@/components/FormBuilder/Settings/CustomerFieldBindingSettings'
import { getQuestionTypeMeta } from '@/components/FormBuilder/question-type-meta'
import { FormPreviewShell } from '@/components/FormPreview/FormPreviewShell'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PaneResizeHandle } from '@/components/ui/pane-resize-handle'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import type { FormPreviewSnapshot } from '@/contracts/form-preview'
import type { QuestionGroupTemplateContract } from '@/contracts/question-groups'
import { saveQuestionGroupTemplate } from '@/lib/data-client/question-groups'
import { createQuestionGroupPreviewSnapshot } from '@/lib/mock-api/question-groups'
import {
  TAIWAN_LANDLINE_EXTENSION_PRESET,
  TAIWAN_LANDLINE_EXTENSION_REGEX,
  TAIWAN_MOBILE_PRESET,
  TAIWAN_MOBILE_REGEX,
} from '@/lib/phone-validation'
import {
  clampPaneWidth,
  getThreePaneGridTemplate,
  questionGroupPaneDefaults,
} from '@/lib/resizable-panes'
import { TAIWAN_ADDRESS_MODE, TAIWAN_ADDRESS_POSTAL_CODE_PRECISION } from '@/lib/taiwan-address'
import { cn } from '@/lib/utils'
import type {
  Choice,
  LogicCondition,
  LogicConditionOperator,
  LogicJump,
  Question,
  QuestionType,
} from '@/types/form'
import { DndContext, type DragEndEvent } from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  AlertTriangle,
  Copy,
  GitBranch,
  GripVertical,
  Plus,
  Save,
  Settings,
  Trash2,
} from 'lucide-react'
import { type CSSProperties, type PointerEvent, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

const operatorLabels: Record<LogicConditionOperator, string> = {
  equals: '等於',
  not_equals: '不等於',
  contains: '包含',
  not_contains: '不包含',
  starts_with: '開頭是',
  ends_with: '結尾是',
  greater_than: '大於',
  less_than: '小於',
  greater_or_equal: '大於或等於',
  less_or_equal: '小於或等於',
  is_answered: '已填答',
  is_not_answered: '未填答',
  includes: '包含選項',
  not_includes: '不包含選項',
  between: '介於',
}

const operatorOptions: LogicConditionOperator[] = [
  'equals',
  'not_equals',
  'contains',
  'not_contains',
  'greater_than',
  'less_than',
  'is_answered',
  'is_not_answered',
]

type QuestionGroupEditorDialogProps = {
  template: QuestionGroupTemplateContract | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: (template: QuestionGroupTemplateContract) => void
}

function choice(questionId: string, label: string, orderIndex: number): Choice {
  return {
    id: crypto.randomUUID(),
    questionId,
    label,
    value: label.toLowerCase().replace(/\s+/g, '-'),
    orderIndex,
  }
}

function defaultChoices(type: QuestionType, questionId: string) {
  if (['MULTIPLE_CHOICE', 'CHECKBOXES', 'DROPDOWN', 'PICTURE_CHOICE', 'RANKING'].includes(type)) {
    return ['選項 1', '選項 2'].map((label, index) => choice(questionId, label, index))
  }
  if (type === 'YES_NO') {
    return ['是', '否'].map((label, index) => choice(questionId, label, index))
  }
  return []
}

function defaultSettings(type: QuestionType): Question['settings'] {
  if (type === 'EMAIL') return { placeholder: 'name@example.com', validation: 'email' }
  if (type === 'PHONE_NUMBER') return { placeholder: '+886 2 1234 5678', validation: 'phone' }
  if (type === 'TAIWAN_MOBILE') {
    return {
      placeholder: '0912345678',
      validation: 'phone',
      validationPreset: TAIWAN_MOBILE_PRESET,
      validationRegex: TAIWAN_MOBILE_REGEX,
      validationTestValue: '0912345678',
    }
  }
  if (type === 'TAIWAN_LANDLINE') {
    return {
      placeholder: '2345-6789',
      validation: 'phone',
      validationPreset: TAIWAN_LANDLINE_EXTENSION_PRESET,
      validationRegex: TAIWAN_LANDLINE_EXTENSION_REGEX,
      validationTestValue: '02-2345-6789#123',
      taiwanLandlineAreaCode: '02',
    }
  }
  if (type === 'TAIWAN_ADDRESS') {
    return {
      showQuestionNumber: true,
      addressMode: TAIWAN_ADDRESS_MODE,
      postalCodePrecision: TAIWAN_ADDRESS_POSTAL_CODE_PRECISION,
      detailPlaceholder: '請輸入路名、段、巷弄、門牌、樓層',
    }
  }
  if (type === 'WEBSITE') return { placeholder: 'https://example.com', validation: 'url' }
  if (type === 'NUMBER') return { numberMin: 0, numberStep: 1 }
  if (type === 'NUMBER_SLIDER') return { sliderMin: 0, sliderMax: 10, sliderStep: 1 }
  if (type === 'RATING') return { steps: 5, style: 'stars' }
  if (type === 'OPINION_SCALE') return { steps: 5, startLabel: '不同意', endLabel: '同意' }
  if (type === 'MATRIX') return { rows: ['項目 1', '項目 2'], columns: ['低', '中', '高'] }
  return { showQuestionNumber: true }
}

function createQuestion(type: QuestionType, templateId: string, orderIndex: number): Question {
  const meta = getQuestionTypeMeta(type)
  const id = `qgq-${crypto.randomUUID()}`
  return {
    id,
    formId: templateId,
    type,
    title: meta.answerable ? `${meta.zhName}問題` : meta.zhName,
    description: meta.description,
    required: meta.answerable,
    settings: defaultSettings(type),
    choices: defaultChoices(type, id),
    orderIndex,
  }
}

function cloneQuestion(question: Question, templateId: string, orderIndex: number): Question {
  const id = `qgq-${crypto.randomUUID()}`
  return {
    ...structuredClone(question),
    id,
    formId: templateId,
    title: `${question.title}（複本）`,
    orderIndex,
    choices: question.choices.map((item) => ({ ...item, id: crypto.randomUUID(), questionId: id })),
  }
}

function normalizeQuestions(questions: Question[], templateId: string) {
  return questions.map((question, orderIndex) => ({
    ...question,
    formId: templateId,
    orderIndex,
    choices: question.choices.map((choice, choiceIndex) => ({
      ...choice,
      questionId: question.id,
      orderIndex: choiceIndex,
    })),
  }))
}

function defaultCondition(question: Question): LogicCondition {
  return {
    field: question.id,
    operator: question.choices.length > 0 ? 'equals' : 'is_answered',
    value: question.choices[0]?.value,
  }
}

function createJump(question: Question, questions: Question[], order: number): LogicJump {
  const nextQuestion = questions.find((item) => item.orderIndex > question.orderIndex)
  return {
    id: `qgl-${crypto.randomUUID()}`,
    questionId: question.id,
    conditions: [defaultCondition(question)],
    operator: 'AND',
    targetQuestionId: nextQuestion?.id ?? null,
    targetGroupId: null,
    order,
  }
}

export function QuestionGroupEditorDialog({
  template,
  open,
  onOpenChange,
  onSaved,
}: QuestionGroupEditorDialogProps) {
  const [draft, setDraft] = useState<QuestionGroupTemplateContract | null>(template)
  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'question' | 'binding' | 'logic'>('question')
  const [pendingDeleteQuestion, setPendingDeleteQuestion] = useState<Question | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [leftPaneWidth, setLeftPaneWidth] = useState(300)
  const [rightPaneWidth, setRightPaneWidth] = useState(360)
  const [leftPaneCollapsed, setLeftPaneCollapsed] = useState(false)
  const [rightPaneCollapsed, setRightPaneCollapsed] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open || !template) return
    const normalizedTemplate = structuredClone(template)
    setDraft(normalizedTemplate)
    setSelectedQuestionId(normalizedTemplate.questions[0]?.id ?? null)
    setActiveTab('question')
    setSettingsOpen(false)
  }, [open, template])

  const selectedQuestion = draft?.questions.find((question) => question.id === selectedQuestionId)
  const previewSnapshot = useMemo(
    () => (draft ? createSelectedQuestionGroupPreviewSnapshot(draft, selectedQuestionId) : null),
    [draft, selectedQuestionId],
  )

  function updateDraft(
    updater: (current: QuestionGroupTemplateContract) => QuestionGroupTemplateContract,
  ) {
    setDraft((current) => (current ? updater(current) : current))
  }

  function updateQuestion(questionId: string, patch: Partial<Question>) {
    updateDraft((current) => ({
      ...current,
      questions: current.questions.map((question) =>
        question.id === questionId ? { ...question, ...patch } : question,
      ),
    }))
  }

  function addQuestion(type: QuestionType) {
    updateDraft((current) => {
      const question = createQuestion(type, current.id, current.questions.length)
      setSelectedQuestionId(question.id)
      setActiveTab('question')
      return {
        ...current,
        questions: normalizeQuestions([...current.questions, question], current.id),
      }
    })
  }

  function duplicateQuestion(question: Question) {
    if (!draft) return
    const duplicated = cloneQuestion(question, draft.id, draft.questions.length)
    updateDraft((current) => ({
      ...current,
      questions: normalizeQuestions([...current.questions, duplicated], current.id),
    }))
    setSelectedQuestionId(duplicated.id)
  }

  function deleteQuestion(questionId: string) {
    updateDraft((current) => {
      const nextQuestions = normalizeQuestions(
        current.questions.filter((question) => question.id !== questionId),
        current.id,
      )
      return {
        ...current,
        questions: nextQuestions,
        logicJumps: current.logicJumps
          .filter((jump) => jump.questionId !== questionId && jump.targetQuestionId !== questionId)
          .map((jump) => ({
            ...jump,
            conditions: jump.conditions.filter((condition) => condition.field !== questionId),
          })),
      }
    })
    setSelectedQuestionId((current) => (current === questionId ? null : current))
    setPendingDeleteQuestion(null)
  }

  function onDragEnd(event: DragEndEvent) {
    if (!draft) return
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = draft.questions.findIndex((question) => question.id === active.id)
    const newIndex = draft.questions.findIndex((question) => question.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    updateDraft((current) => ({
      ...current,
      questions: normalizeQuestions(arrayMove(current.questions, oldIndex, newIndex), current.id),
    }))
  }

  async function saveTemplate() {
    if (!draft) return
    setSaving(true)
    try {
      const saved = await saveQuestionGroupTemplate({
        ...draft,
        questions: normalizeQuestions(draft.questions, draft.id),
      })
      onSaved(saved)
      onOpenChange(false)
      toast.success('已儲存題組')
    } catch (error) {
      console.error('[MiaoForm] save question group template failed', error)
      toast.error('目前無法儲存題組，請稍後再試。')
    } finally {
      setSaving(false)
    }
  }

  function startPaneResize(pane: 'left' | 'right', event: PointerEvent<HTMLButtonElement>) {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = pane === 'left' ? leftPaneWidth : rightPaneWidth
    const config =
      pane === 'left' ? questionGroupPaneDefaults.left : questionGroupPaneDefaults.right
    const setWidth = pane === 'left' ? setLeftPaneWidth : setRightPaneWidth
    const setCollapsed = pane === 'left' ? setLeftPaneCollapsed : setRightPaneCollapsed

    function onPointerMove(moveEvent: globalThis.PointerEvent) {
      const delta = moveEvent.clientX - startX
      const nextWidth = pane === 'left' ? startWidth + delta : startWidth - delta
      setCollapsed(false)
      setWidth(clampPaneWidth(nextWidth, config))
    }

    function onPointerUp() {
      window.removeEventListener('pointermove', onPointerMove)
    }

    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp, { once: true })
  }

  const editorGridColumns = getThreePaneGridTemplate({
    leftWidth: leftPaneWidth,
    rightWidth: rightPaneWidth,
    leftCollapsed: leftPaneCollapsed,
    rightCollapsed: rightPaneCollapsed,
    centerMinWidth: 0,
    leftConfig: questionGroupPaneDefaults.left,
    rightConfig: questionGroupPaneDefaults.right,
  })

  if (!draft) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="left-[10px] top-[10px] h-[calc(100dvh-20px)] max-h-none w-[calc(100dvw-20px)] max-w-none translate-x-0 translate-y-0 overflow-hidden p-0">
          <div className="grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)] bg-slate-50">
            <div className="border-b border-slate-200 bg-white p-5 pr-14">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <DialogHeader>
                  <DialogTitle>題組問題管理</DialogTitle>
                  <DialogDescription className="sr-only">題組問題管理</DialogDescription>
                </DialogHeader>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{draft.questions.length} 題</Badge>
                  <Button type="button" onClick={saveTemplate} disabled={saving}>
                    <Save className="size-4" />
                    {saving ? '儲存中...' : '儲存題組'}
                  </Button>
                </div>
              </div>
            </div>
            <div
              data-testid="question-group-editor-grid"
              className="grid min-h-0 grid-rows-[220px_minmax(0,1fr)_320px] transition-[grid-template-columns] duration-200 ease-out lg:grid-cols-[var(--question-group-editor-grid-columns)] lg:grid-rows-1"
              style={
                {
                  '--question-group-editor-grid-columns': editorGridColumns,
                } as CSSProperties
              }
            >
              <div className="relative min-h-0 min-w-0 overflow-hidden">
                <QuestionGroupEditorList
                  questions={draft.questions}
                  selectedQuestionId={selectedQuestionId}
                  onSelect={setSelectedQuestionId}
                  onAdd={addQuestion}
                  onDuplicate={duplicateQuestion}
                  onDelete={setPendingDeleteQuestion}
                  onDragEnd={onDragEnd}
                  onOpenSettings={() => setSettingsOpen(true)}
                />
                <PaneResizeHandle
                  edge="right"
                  collapsed={leftPaneCollapsed}
                  resizeLabel="拖曳調整題組左欄寬度"
                  collapseLabel="收合題組左欄"
                  expandLabel="展開題組左欄"
                  onResizeStart={(event) => startPaneResize('left', event)}
                  onToggle={() => setLeftPaneCollapsed((collapsed) => !collapsed)}
                />
              </div>
              <section className="min-h-0 border-b border-slate-200 lg:border-r lg:border-b-0">
                <QuestionGroupPreview snapshot={previewSnapshot} />
              </section>
              <div className="relative min-h-0 min-w-0 overflow-hidden">
                <QuestionGroupSettingsPanel
                  questions={draft.questions}
                  logicJumps={draft.logicJumps}
                  selectedQuestion={selectedQuestion}
                  activeTab={activeTab}
                  onActiveTabChange={setActiveTab}
                  onQuestionChange={updateQuestion}
                  onLogicChange={(logicJumps) =>
                    updateDraft((current) => ({ ...current, logicJumps }))
                  }
                />
                <PaneResizeHandle
                  edge="left"
                  collapsed={rightPaneCollapsed}
                  resizeLabel="拖曳調整題組右欄寬度"
                  collapseLabel="收合題組右欄"
                  expandLabel="展開題組右欄"
                  onResizeStart={(event) => startPaneResize('right', event)}
                  onToggle={() => setRightPaneCollapsed((collapsed) => !collapsed)}
                />
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={Boolean(pendingDeleteQuestion)}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setPendingDeleteQuestion(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>移除「{pendingDeleteQuestion?.title}」？</AlertDialogTitle>
            <AlertDialogDescription>
              此操作只會從題組模板移除此問題，不會刪除已經建立複本到問卷中的題目。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button type="button" variant="outline">
                取消
              </Button>
            </AlertDialogCancel>
            <AlertDialogAction asChild>
              <Button
                type="button"
                variant="destructive"
                onClick={() => pendingDeleteQuestion && deleteQuestion(pendingDeleteQuestion.id)}
              >
                確認移除
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <QuestionGroupTemplateSettingsDialog
        open={settingsOpen}
        template={draft}
        onOpenChange={setSettingsOpen}
        onTemplateChange={(patch) =>
          updateDraft((current) => ({
            ...current,
            ...patch,
          }))
        }
      />
    </>
  )
}

function QuestionGroupEditorList({
  questions,
  selectedQuestionId,
  onSelect,
  onAdd,
  onDuplicate,
  onDelete,
  onDragEnd,
  onOpenSettings,
}: {
  questions: Question[]
  selectedQuestionId: string | null
  onSelect: (questionId: string) => void
  onAdd: (type: QuestionType) => void
  onDuplicate: (question: Question) => void
  onDelete: (question: Question) => void
  onDragEnd: (event: DragEndEvent) => void
  onOpenSettings: () => void
}) {
  return (
    <aside className="flex h-full min-h-0 flex-col overflow-hidden border-b border-slate-200 bg-white lg:border-r lg:border-b-0">
      <div className="flex h-14 shrink-0 items-center justify-between gap-3 border-b border-slate-200 px-4">
        <div className="min-w-0">
          <div className="font-semibold text-slate-950">問題清單</div>
          <div className="text-xs text-slate-500">{questions.length} 個題組問題</div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            aria-label="編輯題組設定"
            onClick={onOpenSettings}
          >
            <Settings className="size-4" />
          </Button>
          <QuestionTypeSelector
            onSelect={onAdd}
            trigger={
              <Button type="button" size="icon" variant="ghost" aria-label="新增題組問題">
                <Plus className="size-4" />
              </Button>
            }
          />
        </div>
      </div>
      <ScrollArea
        className="min-h-0 flex-1"
        viewportClassName="p-3"
        viewportDataTestId="question-group-left-scroll"
      >
        {questions.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm leading-6 text-slate-500">
            這個題組還沒有問題。點擊右上角加號選擇題型。
          </div>
        ) : (
          <DndContext onDragEnd={onDragEnd}>
            <SortableContext
              items={questions.map((question) => question.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {questions.map((question, index) => (
                  <QuestionGroupEditorListItem
                    key={question.id}
                    question={question}
                    index={index}
                    selected={question.id === selectedQuestionId}
                    onSelect={() => onSelect(question.id)}
                    onDuplicate={() => onDuplicate(question)}
                    onDelete={() => onDelete(question)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </ScrollArea>
    </aside>
  )
}

function QuestionGroupEditorListItem({
  question,
  index,
  selected,
  onSelect,
  onDuplicate,
  onDelete,
}: {
  question: Question
  index: number
  selected: boolean
  onSelect: () => void
  onDuplicate: () => void
  onDelete: () => void
}) {
  const sortable = useSortable({ id: question.id })
  const meta = getQuestionTypeMeta(question.type)
  return (
    <div
      ref={sortable.setNodeRef}
      style={{
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition,
      }}
      className={cn(
        'group rounded-md border bg-white p-3 transition-colors',
        selected ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-indigo-200',
      )}
    >
      <button type="button" className="w-full text-left" onClick={onSelect}>
        <div className="flex items-center gap-2">
          <span
            className="cursor-grab text-slate-400"
            aria-label="拖拉排序"
            {...sortable.attributes}
            {...sortable.listeners}
          >
            <GripVertical className="size-4" />
          </span>
          <span className="grid size-6 shrink-0 place-items-center rounded bg-slate-100 text-xs font-semibold">
            {index + 1}
          </span>
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-950">
            {question.title || '未命名問題'}
          </span>
        </div>
      </button>
      <div className="mt-3 flex h-8 items-center justify-between gap-2">
        <span
          className="grid size-7 shrink-0 place-items-center rounded-full border border-slate-200 bg-slate-50 text-slate-500"
          role="img"
          aria-label={meta.zhName}
          title={meta.zhName}
        >
          <meta.icon className="size-3.5" aria-hidden="true" />
        </span>
        <div className="flex opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="複製問題"
            onClick={onDuplicate}
          >
            <Copy className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="移除問題"
            className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
            onClick={onDelete}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

function QuestionGroupPreview({
  snapshot,
}: {
  snapshot: FormPreviewSnapshot | null
}) {
  if (!snapshot || snapshot.questions.length === 0) {
    return (
      <div className="grid h-full min-h-0 place-items-center bg-slate-50 p-8 text-center">
        <div>
          <GitBranch className="mx-auto size-10 text-slate-300" />
          <h3 className="mt-4 font-semibold text-slate-950">選擇或新增題組問題</h3>
          <p className="mt-2 text-sm text-slate-500">中間主畫面會呈現題組問題的填答預覽。</p>
        </div>
      </div>
    )
  }

  return (
    <section className="flex h-full min-h-0 flex-col overflow-hidden bg-slate-100">
      <div
        className="min-h-0 flex-1 overflow-y-auto p-4"
        data-testid="question-group-preview-scroll"
      >
        <FormPreviewShell
          snapshot={snapshot}
          mode="full_flow"
          showToolbar={false}
          showStepNavigator={false}
          showOnePageSubmitBar={false}
        />
      </div>
    </section>
  )
}

function QuestionGroupTemplateSettingsDialog({
  open,
  template,
  onOpenChange,
  onTemplateChange,
}: {
  open: boolean
  template: QuestionGroupTemplateContract
  onOpenChange: (open: boolean) => void
  onTemplateChange: (patch: Partial<QuestionGroupTemplateContract>) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>編輯題組設定</DialogTitle>
          <DialogDescription>
            調整題組基本資訊、分類欄位與標籤欄位。這些設定只會更新 mock 題組資料。
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-5 md:grid-cols-2">
          <section className="space-y-4 rounded-lg border border-slate-200 p-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-950">基本資訊</h3>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                題組名稱與描述會顯示在題組管理清單中，方便辨識用途。
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="question-group-template-title">題組名稱</Label>
              <Input
                id="question-group-template-title"
                value={template.title}
                onChange={(event) => onTemplateChange({ title: event.currentTarget.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="question-group-template-description">題組描述</Label>
              <Textarea
                id="question-group-template-description"
                value={template.description ?? ''}
                className="min-h-28"
                onChange={(event) => onTemplateChange({ description: event.currentTarget.value })}
              />
            </div>
          </section>
          <section className="space-y-4 rounded-lg border border-slate-200 p-4">
            <div>
              <h3 className="text-sm font-semibold text-slate-950">分類與標籤</h3>
              <p className="mt-1 text-xs leading-5 text-slate-500">
                分類用於主要歸檔，標籤用於跨分類搜尋與重用。
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="question-group-template-category">分類</Label>
              <Input
                id="question-group-template-category"
                value={template.category ?? ''}
                onChange={(event) => onTemplateChange({ category: event.currentTarget.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="question-group-template-tags">標籤</Label>
              <Input
                id="question-group-template-tags"
                value={template.tags.join(', ')}
                onChange={(event) =>
                  onTemplateChange({ tags: parseTagsInput(event.currentTarget.value) })
                }
              />
              <p className="text-xs text-slate-500">使用逗號分隔多個標籤。</p>
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function parseTagsInput(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

export function createSelectedQuestionGroupPreviewSnapshot(
  template: QuestionGroupTemplateContract,
  selectedQuestionId: string | null,
): FormPreviewSnapshot {
  const selectedQuestion = template.questions.find((question) => question.id === selectedQuestionId)
  const questions = selectedQuestion ? [selectedQuestion] : []
  const questionIds = new Set(questions.map((question) => question.id))
  return createQuestionGroupPreviewSnapshot({
    ...template,
    questionIds: questions.map((question) => question.id),
    questions,
    logicJumps: template.logicJumps.filter(
      (jump) =>
        questionIds.has(jump.questionId) &&
        (jump.targetQuestionId === null || questionIds.has(jump.targetQuestionId)),
    ),
  })
}

function QuestionGroupSettingsPanel({
  questions,
  logicJumps,
  selectedQuestion,
  activeTab,
  onActiveTabChange,
  onQuestionChange,
  onLogicChange,
}: {
  questions: Question[]
  logicJumps: LogicJump[]
  selectedQuestion?: Question
  activeTab: 'question' | 'binding' | 'logic'
  onActiveTabChange: (tab: 'question' | 'binding' | 'logic') => void
  onQuestionChange: (questionId: string, patch: Partial<Question>) => void
  onLogicChange: (logicJumps: LogicJump[]) => void
}) {
  return (
    <aside className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
      <Tabs
        value={activeTab}
        onValueChange={(value) => onActiveTabChange(value as 'question' | 'binding' | 'logic')}
        className="flex min-h-0 flex-1 flex-col"
      >
        <div className="shrink-0 border-b border-slate-200 p-3">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="question">問題</TabsTrigger>
            <TabsTrigger value="binding">綁定</TabsTrigger>
            <TabsTrigger value="logic">邏輯</TabsTrigger>
          </TabsList>
        </div>
        <ScrollArea
          className="min-h-0 flex-1"
          viewportClassName="p-4"
          viewportDataTestId="question-group-right-scroll"
        >
          <TabsContent value="question" className="mt-0">
            {selectedQuestion ? (
              <QuestionSettingsFields
                question={selectedQuestion}
                questions={questions}
                onQuestionChange={(patch) => onQuestionChange(selectedQuestion.id, patch)}
                onSettingsChange={(patch) =>
                  onQuestionChange(selectedQuestion.id, {
                    settings: { ...selectedQuestion.settings, ...patch },
                  })
                }
              />
            ) : (
              <EmptySidePanel message="請先選擇左欄題目。" />
            )}
          </TabsContent>
          <TabsContent value="binding" className="mt-0">
            {selectedQuestion ? (
              <CustomerFieldBindingSettings question={selectedQuestion} />
            ) : (
              <EmptySidePanel message="請先選擇左欄題目再設定新客申請欄位綁定。" />
            )}
          </TabsContent>
          <TabsContent value="logic" className="mt-0">
            {selectedQuestion ? (
              <QuestionGroupLogicPanel
                questions={questions}
                selectedQuestion={selectedQuestion}
                logicJumps={logicJumps}
                onLogicChange={onLogicChange}
              />
            ) : (
              <EmptySidePanel message="請先選擇左欄題目再設定邏輯。" />
            )}
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </aside>
  )
}

function EmptySidePanel({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
      {message}
    </div>
  )
}

function QuestionGroupLogicPanel({
  questions,
  selectedQuestion,
  logicJumps,
  onLogicChange,
}: {
  questions: Question[]
  selectedQuestion: Question
  logicJumps: LogicJump[]
  onLogicChange: (logicJumps: LogicJump[]) => void
}) {
  const jumps = useMemo(
    () =>
      logicJumps
        .filter((jump) => jump.questionId === selectedQuestion.id)
        .sort((left, right) => left.order - right.order),
    [logicJumps, selectedQuestion.id],
  )

  function addJump() {
    onLogicChange([...logicJumps, createJump(selectedQuestion, questions, jumps.length)])
  }

  function updateJump(id: string, patch: Partial<LogicJump>) {
    onLogicChange(logicJumps.map((jump) => (jump.id === id ? { ...jump, ...patch } : jump)))
  }

  function deleteJump(id: string) {
    onLogicChange(logicJumps.filter((jump) => jump.id !== id))
  }

  function updateCondition(
    jump: LogicJump,
    conditionIndex: number,
    patch: Partial<LogicCondition>,
  ) {
    updateJump(jump.id, {
      conditions: jump.conditions.map((condition, index) =>
        index === conditionIndex ? { ...condition, ...patch } : condition,
      ),
    })
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm leading-6 text-slate-600">
        跳轉目標限制在此題組內的題目，或選擇「結束題組」。這裡不會列出問卷外部題目。
      </div>
      {jumps.length === 0 && (
        <div className="rounded-md border border-dashed border-slate-300 p-4 text-sm text-slate-500">
          尚未建立題組邏輯。可新增規則來決定此題後要跳到題組內哪一題。
        </div>
      )}
      {jumps.map((jump, jumpIndex) => (
        <div key={jump.id} className="space-y-3 rounded-lg border border-slate-200 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-950">
              <GitBranch className="size-4 text-indigo-600" />
              規則 {jumpIndex + 1}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="text-rose-600 hover:bg-rose-50 hover:text-rose-700"
              onClick={() => deleteJump(jump.id)}
              aria-label="刪除規則"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
          {jump.conditions.map((condition, conditionIndex) => {
            const conditionQuestion = questions.find((question) => question.id === condition.field)
            return (
              <div key={`${jump.id}-${conditionIndex}`} className="space-y-2">
                <Label>條件 {conditionIndex + 1}</Label>
                <Select
                  value={condition.field}
                  onValueChange={(field) => updateCondition(jump, conditionIndex, { field })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {questions.map((question, index) => (
                      <SelectItem key={question.id} value={question.id}>
                        {index + 1}. {question.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={condition.operator}
                  onValueChange={(operator) =>
                    updateCondition(jump, conditionIndex, {
                      operator: operator as LogicConditionOperator,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {operatorOptions.map((operator) => (
                      <SelectItem key={operator} value={operator}>
                        {operatorLabels[operator]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!['is_answered', 'is_not_answered'].includes(condition.operator) && (
                  <ConditionValueInput
                    condition={condition}
                    question={conditionQuestion}
                    onChange={(value) => updateCondition(jump, conditionIndex, { value })}
                  />
                )}
              </div>
            )
          })}
          <Separator />
          <div className="space-y-2">
            <Label>跳轉目標</Label>
            <Select
              value={jump.targetQuestionId ?? '__end_group__'}
              onValueChange={(target) =>
                updateJump(jump.id, {
                  targetQuestionId: target === '__end_group__' ? null : target,
                  targetGroupId: null,
                })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__end_group__">結束題組</SelectItem>
                {questions.map((question, index) => (
                  <SelectItem key={question.id} value={question.id}>
                    {index + 1}. {question.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" className="w-full" onClick={addJump}>
        <Plus className="size-4" />
        新增規則
      </Button>
      {questions.length === 0 && (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          <AlertTriangle className="mt-0.5 size-4" />
          請先新增題組問題，再設定題組邏輯。
        </div>
      )}
    </div>
  )
}

function ConditionValueInput({
  condition,
  question,
  onChange,
}: {
  condition: LogicCondition
  question?: Question
  onChange: (value: LogicCondition['value']) => void
}) {
  if (question?.choices.length) {
    return (
      <Select value={String(condition.value ?? '')} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="選擇比對值" />
        </SelectTrigger>
        <SelectContent>
          {question.choices.map((choice) => (
            <SelectItem key={choice.id} value={choice.value}>
              {choice.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    )
  }

  return (
    <Input
      value={String(condition.value ?? '')}
      onChange={(event) => onChange(event.target.value)}
      placeholder="輸入比對值"
    />
  )
}
